/**
 * Maintenance API Service
 *
 * Calls the BI portal's own /api/maintenance/* proxy endpoints (same origin).
 * The portal backend forwards these server-side to the website-template backend,
 * which eliminates all browser CORS and TLS mixed-content errors.
 */

import { apiFetch } from './httpClient';

// Empty string = relative to current origin (portal.novosolution.org in prod,
// localhost:3000 in dev).  No cross-origin calls from the browser.
const BACKEND_URL = '';

class MaintenanceAPI {
  /**
   * Get all maintenance tasks
   */
  async getTasks() {
    return apiFetch(
      `${BACKEND_URL}/api/maintenance/tasks`,
      {},
      { fallbackMessage: 'Failed to fetch maintenance tasks' }
    );
  }

  /**
   * Get specific task details
   */
  async getTask(taskId) {
    return apiFetch(
      `${BACKEND_URL}/api/maintenance/tasks/${taskId}`,
      {},
      { fallbackMessage: 'Failed to fetch maintenance task' }
    );
  }

  /**
   * Update task configuration
   */
  async updateTask(taskId, data) {
    return apiFetch(
      `${BACKEND_URL}/api/maintenance/tasks/${taskId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      },
      { fallbackMessage: 'Failed to update maintenance task' }
    );
  }

  /**
   * Run task manually
   */
  async runTask(taskId) {
    return apiFetch(
      `${BACKEND_URL}/api/maintenance/tasks/${taskId}/run`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      },
      { fallbackMessage: 'Failed to run maintenance task' }
    );
  }

  /**
   * Get system health metrics
   */
  async getHealth() {
    return apiFetch(
      `${BACKEND_URL}/api/maintenance/health`,
      {},
      { fallbackMessage: 'Failed to fetch maintenance health metrics' }
    );
  }

  /**
   * Get maintenance logs
   */
  async getLogs(params = {}) {
    const queryParams = new URLSearchParams(params);
    return apiFetch(
      `${BACKEND_URL}/api/maintenance/logs?${queryParams}`,
      {},
      { fallbackMessage: 'Failed to fetch maintenance logs' }
    );
  }

  /**
   * Get logs for specific task
   */
  async getTaskLogs(taskName, date = null) {
    const params = { task: taskName };
    if (date) params.date = date;
    
    return await this.getLogs(params);
  }
}

export const maintenanceApi = new MaintenanceAPI();

